import { Container, Row, Col, Button, ButtonGroup } from "react-bootstrap";
import { Sidebar } from "./Sidebar";
import { useState } from "react";
import { IdeaCard } from "./IdeaCard";
import { useApiGet } from "../utils/api";
import { useParams } from "react-router";
import { AddIdeaModal } from "./AddIdeaModal";
import { groupBy, times } from "lodash";
import { SettingsModal } from "./SettingsModal";
import { Link } from "react-router-dom";
import { useAppContext } from "../contexts/AppContext";
import { useModal } from "../utils/modal_launcher";

export function Board() {
  const { id } = useParams();
  const { data, unavailable, refetch } = useApiGet(`/api/user/board/${id}`);
  const settingsModal = useModal(SettingsModal, { refetch });
  const [columns, setColumns] = useState(4);
  const [showAddIdeaModal, setShowAddIdeaModal] = useState(false);
  const { searchTerm, voteFilter } = useAppContext();
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});

  if (unavailable) {
    return unavailable;
  }

  const { board } = data;

  let groups = groupBy(
    board.ideas,
    (idea: any) => board.family.find((f: any) => f.id === idea.board_id)?.name
  );

  groups = Object.fromEntries(
    Object.entries(groups).map(([groupName, ideas]) => [
      groupName,
      ideas
        .filter((idea: any) =>
          JSON.stringify(Object.values(idea))
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
        )
        .filter((idea: any) => !voteFilter || idea.votes_summary[voteFilter]?.length > 0),
    ])
  );

  return (
    <div style={{ height: "calc(100vh - 56px)" }}>
      <Container fluid className="h-100">
        <Row className="h-100">
          <Col md={2} className="bg-light p-0 border-end">
            <Sidebar board={board} refetch={refetch} />
          </Col>
          <Col md={10} className="p-0">
            <div className="d-flex flex-column h-100">
              <Container fluid className="flex-grow-1 p-4">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h2 className="mb-0 flex-1">{board.name}</h2>
                  <div className="d-flex gap-3 align-items-center flex-1 justify-content-end fs-14">
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => setShowAddIdeaModal(true)}
                      className="mr-3"
                    >
                      + Idea
                    </Button>
                    <div className="d-flex align-items-center">
                      Columns:
                      <ButtonGroup size="sm">
                        {[3, 4, 5, 6].map((num) => (
                          <div
                            key={num}
                            className={`${columns === num ? "fw-bold" : ""} ph-1 pointer`}
                            onClick={() => setColumns(num)}
                          >
                            {num}
                          </div>
                        ))}
                      </ButtonGroup>
                    </div>
                    <Button
                      variant="link"
                      className="btn-text ml-2"
                      onClick={() => settingsModal.show({ board })}
                    >
                      <i className="fas fa-cog black" />
                    </Button>
                  </div>
                </div>

                {board.ideas.length === 0 && (
                  <Button variant="link gray" onClick={() => setShowAddIdeaModal(true)}>
                    Add an idea
                  </Button>
                )}

                {Object.entries(groups).map(([boardName, ideas]) => (
                  <>
                    {ideas[0]?.board_id !== board.id && (
                      <>
                        <div className="d-flex align-items-center mt-4 mb-3 ">
                          <Link
                            to={`/boards/${ideas[0]?.board_id}`}
                            className="h3 no-underline d-block black"
                          >
                            {boardName}
                          </Link>
                          <i
                            className={`fas fa-angle-${
                              collapsedGroups[boardName] ? "right" : "down"
                            } ml-3 pointer gray-8`}
                            onClick={(e) => {
                              e.preventDefault();
                              setCollapsedGroups((prev) => ({
                                ...prev,
                                [boardName]: !prev[boardName],
                              }));
                            }}
                          />
                        </div>
                      </>
                    )}
                    {!collapsedGroups[boardName] && (
                      <Row xs={1} md={columns} className="g-3">
                        {times(columns, (colIndex) => (
                          <Col key={colIndex}>
                            {ideas
                              .filter((_, i) => i % columns === colIndex)
                              .map((idea: any) => (
                                <div key={idea.id} className="mb-3">
                                  <IdeaCard idea={idea} board={board} refetch={refetch} />
                                </div>
                              ))}
                          </Col>
                        ))}
                      </Row>
                    )}
                  </>
                ))}
              </Container>
            </div>
          </Col>
        </Row>
      </Container>

      {showAddIdeaModal && (
        <AddIdeaModal
          board={board}
          close={() => setShowAddIdeaModal(false)}
          refetch={refetch}
        />
      )}
    </div>
  );
}
